import { useEffect, useState } from "react";
import { api, fmtBps, fmtPct } from "@/lib/api";
import { PageHeader, Btn, Modal, Input, Select, Badge, Section } from "@/components/Shared";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

const EMPTY = {
  program_id: "", merchant_id: "", merchant_name: "", gateway: "Cashfree", lob: "Fastag",
  active: true, currency: "INR", base_rate_type: "Basis Points", base_rate: 0.0092,
  merchant_rate_type: "Basis Points", merchant_rate: 0.0102, referred_partner_id: "P000",
  commission_structure: "Percentage", partner_share_value: 0,
};

export default function Programs() {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [meta, setMeta] = useState({ gateways: [], lobs: [], rate_types: [], commission_structures: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [r, p, m, mt] = await Promise.all([
      api.get("/programs"), api.get("/partners"), api.get("/merchants"), api.get("/meta"),
    ]);
    setRows(r.data.sort((a, b) => a.program_id.localeCompare(b.program_id)));
    setPartners(p.data); setMerchants(m.data); setMeta(mt.data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    const next = `PRG${String(rows.length + 1).padStart(3, "0")}`;
    setForm({ ...EMPTY, program_id: next });
    setEditId(null); setOpen(true);
  };
  const openEdit = (r) => { setForm({ ...r }); setEditId(r.program_id); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        base_rate: Number(form.base_rate),
        merchant_rate: Number(form.merchant_rate),
        partner_share_value: Number(form.partner_share_value),
      };
      if (editId) await api.put(`/programs/${editId}`, payload);
      else await api.post("/programs", payload);
      toast.success(editId ? "Program updated" : "Program created");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (pid) => {
    if (!await openConfirm({ title: `Delete program ${pid}?`, message: "This program and all its volume entries will be permanently deleted.", confirmLabel: "Delete program", danger: true })) return;
    await api.delete(`/programs/${pid}`);
    toast.success("Deleted"); load();
  };

  const renderShare = (r) => {
    if (r.commission_structure === "Basis Points") {
      return <span className="num">{(r.partner_share_value * 100).toFixed(2)}% <span className="text-[10px] text-[var(--muted)]">of volume</span></span>;
    }
    return <span className="num">{fmtPct(r.partner_share_value)} <span className="text-[10px] text-[var(--muted)]">of revenue</span></span>;
  };

  return (
    <div data-testid="programs-page">
      <PageHeader
        title="Commercial Setup"
        actions={<Btn variant="accent" testid="add-program-btn" onClick={openNew}><Plus size={14} /> New Program</Btn>}
      />
      <div className="p-4 sm:p-10">
        <Section title={`${rows.length} program(s) configured`} testid="programs-section">
          <div className="overflow-auto">
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr>
                <th>Merchant ID</th><th>Merchant</th><th>Gateway</th><th>LOB</th>
                <th className="text-right">Base</th><th className="text-right">Billing</th>
                <th>Partner</th><th>Structure</th><th className="text-right">Share</th>
                <th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const partner = partners.find((p) => p.partner_id === r.referred_partner_id);
                  return (
                    <tr key={r.program_id} data-testid={`program-row-${r.program_id}`}>
                      <td className="num">{r.merchant_id}</td>
                      <td className="font-medium">{r.merchant_name}</td>
                      <td>{r.gateway}</td>
                      <td>{r.lob}</td>
                      <td className="text-right num">{fmtBps(r.base_rate)}</td>
                      <td className="text-right num">{fmtBps(r.merchant_rate)}</td>
                      <td>{partner?.partner_name || r.referred_partner_id}</td>
                      <td>{r.commission_structure === "Basis Points" ? <Badge tone="accent">% of Vol</Badge> : <Badge tone="muted">% of Rev</Badge>}</td>
                      <td className="text-right">{renderShare(r)}</td>
                      <td>{r.active ? <Badge tone="positive">Active</Badge> : <Badge tone="muted">Inactive</Badge>}</td>
                      <td className="text-right">
                        <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm" data-testid={`edit-${r.program_id}`}><Pencil size={13} /></button>
                        <button onClick={() => remove(r.program_id)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm" data-testid={`delete-${r.program_id}`}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </Section>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Program" : "New Program"} testid="program-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Gateway</label><Select testid="form-gateway" options={meta.gateways} value={form.gateway} onChange={(v) => setForm({ ...form, gateway: v })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">LOB</label><Select testid="form-lob" options={meta.lobs} value={form.lob} onChange={(v) => setForm({ ...form, lob: v })} /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Merchant</label>
            <Select testid="form-merchant" options={merchants.map((m) => ({ value: m.merchant_id, label: `${m.merchant_name} · ${m.merchant_id}` }))} value={form.merchant_id}
              onChange={(v) => { const m = merchants.find((x) => x.merchant_id === v); setForm({ ...form, merchant_id: v, merchant_name: m?.merchant_name || "" }); }} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Base Rate Type</label><Select options={meta.rate_types} value={form.base_rate_type} onChange={(v) => setForm({ ...form, base_rate_type: v })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Base Rate (decimal, e.g. 0.0092 = 0.92%)</label><Input type="number" step="0.0001" data-testid="form-base-rate" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Merchant Rate Type</label><Select options={meta.rate_types} value={form.merchant_rate_type} onChange={(v) => setForm({ ...form, merchant_rate_type: v })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Merchant Rate</label><Input type="number" step="0.0001" data-testid="form-merchant-rate" value={form.merchant_rate} onChange={(e) => setForm({ ...form, merchant_rate: e.target.value })} /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Referred Partner</label>
            <Select testid="form-partner" options={partners.map((p) => ({ value: p.partner_id, label: `${p.partner_id} · ${p.partner_name}` }))} value={form.referred_partner_id} onChange={(v) => setForm({ ...form, referred_partner_id: v })} /></div>

          {/* Share toggle */}
          <div className="col-span-2 border-t border-[var(--line)] pt-3 mt-1">
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Commission Structure</label>
            <div className="flex gap-2 mt-1.5" role="radiogroup" data-testid="structure-toggle">
              {[
                { v: "Percentage", l: "% of Revenue", hint: "Partner gets X% of (Billing − Cost). e.g. 0.5 = 50%" },
                { v: "Basis Points", l: "% of Volume", hint: "Partner gets X% directly off processed volume. e.g. 0.0002 = 0.02%" },
              ].map((opt) => (
                <button key={opt.v} type="button" onClick={() => setForm({ ...form, commission_structure: opt.v, partner_share_value: 0 })}
                  data-testid={`structure-${opt.v.toLowerCase().replace(' ', '-')}`}
                  className={`flex-1 px-3 py-2.5 text-[12.5px] border rounded-sm text-left transition-colors ${form.commission_structure === opt.v ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--line-2)] bg-[var(--surface)] hover:border-[var(--ink-2)]"}`}>
                  <div className="font-semibold">{opt.l}</div>
                  <div className={`text-[10.5px] mt-0.5 ${form.commission_structure === opt.v ? "text-white/60" : "text-[var(--muted)]"}`}>{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">
              {form.commission_structure === "Basis Points" ? "Partner Share (decimal of volume — e.g. 0.0002 for 0.02%)" : "Partner Share (decimal of revenue — e.g. 0.5 for 50%)"}
            </label>
            <Input type="number" step="0.0001" data-testid="form-share-value" value={form.partner_share_value} onChange={(e) => setForm({ ...form, partner_share_value: e.target.value })} />
            <div className="text-[11px] text-[var(--muted)] mt-1">
              {form.commission_structure === "Basis Points"
                ? `≈ ${(Number(form.partner_share_value || 0) * 100).toFixed(2)}% of volume`
                : `≈ ${(Number(form.partner_share_value || 0) * 100).toFixed(2)}% of revenue`}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" testid="save-program" onClick={save}>{editId ? "Save changes" : "Create program"}</Btn>
        </div>
      </Modal>
    </div>
  );
}
